// Frontend permissions utility - mirrors backend permissions

export type SystemRole = 'admin' | 'member' | 'guest';
export type ProjectRole = 'owner' | 'editor' | 'commenter' | 'viewer';
export type TeamRole = 'owner' | 'admin' | 'member';

// System-level permissions
export type SystemPermission =
  | 'user:create'
  | 'user:read'
  | 'user:update'
  | 'user:delete'
  | 'user:update_own'
  | 'team:create'
  | 'task:create';

// Project-level permissions
export type ProjectPermission =
  | 'task:create'
  | 'task:read'
  | 'task:update'
  | 'task:delete'
  | 'task:assign'
  | 'task:complete'
  | 'comment:create'
  | 'comment:delete'
  | 'attachment:upload'
  | 'attachment:delete'
  | 'section:create'
  | 'section:update'
  | 'section:delete'
  | 'project:update'
  | 'project:delete'
  | 'project:manage_members';

// Team-level permissions
export type TeamPermission =
  | 'team:view'
  | 'team:update'
  | 'team:delete'
  | 'team:manage_members'
  | 'team:add_member'
  | 'team:remove_member'
  | 'team:create_project';

// Permission mappings
const SystemPermissions: Record<SystemPermission, SystemRole[]> = {
  'user:create': ['admin'],
  'user:read': ['admin', 'member', 'guest'],
  'user:update': ['admin'],
  'user:delete': ['admin'],
  'user:update_own': ['admin', 'member', 'guest'],
  'team:create': ['admin', 'member'],
  'task:create': ['admin', 'member'],
};

const ProjectPermissions: Record<ProjectPermission, ProjectRole[]> = {
  'task:create': ['owner', 'editor'],
  'task:read': ['owner', 'editor', 'commenter', 'viewer'],
  'task:update': ['owner', 'editor'],
  'task:delete': ['owner', 'editor'],
  'task:assign': ['owner', 'editor'],
  'task:complete': ['owner', 'editor'],
  'comment:create': ['owner', 'editor', 'commenter'],
  'comment:delete': ['owner', 'editor'],
  'attachment:upload': ['owner', 'editor', 'commenter'],
  'attachment:delete': ['owner', 'editor'],
  'section:create': ['owner', 'editor'],
  'section:update': ['owner', 'editor'],
  'section:delete': ['owner', 'editor'],
  'project:update': ['owner', 'editor'],
  'project:delete': ['owner'],
  'project:manage_members': ['owner'],
};

const TeamPermissions: Record<TeamPermission, TeamRole[]> = {
  'team:view': ['owner', 'admin', 'member'],
  'team:update': ['owner', 'admin'],
  'team:delete': ['owner'],
  'team:manage_members': ['owner', 'admin'],
  'team:add_member': ['owner', 'admin'],
  'team:remove_member': ['owner', 'admin'],
  'team:create_project': ['owner', 'admin', 'member'],
};

// Check if a system role has a specific system permission
export function hasSystemPermission(
  role: SystemRole | undefined,
  permission: SystemPermission,
): boolean {
  if (!role) return false;
  // Admin always has all permissions
  if (role === 'admin') return true;
  return SystemPermissions[permission]?.includes(role) ?? false;
}

// Check if a project role has a specific project permission
export function hasProjectPermission(
  role: ProjectRole | undefined,
  permission: ProjectPermission,
): boolean {
  if (!role) return false;
  // Owner always has all project permissions
  if (role === 'owner') return true;
  return ProjectPermissions[permission]?.includes(role) ?? false;
}

type ProjectAccessSummary = {
  id: string;
  owner_id?: string;
  user_role?: string | null;
};

type TaskProjectSummary = {
  project_id?: string;
  projects?: Array<{ id: string }>;
  permissions?: { can_update?: boolean };
};

export function isProjectRole(role: string | null | undefined): role is ProjectRole {
  return role === 'owner' || role === 'editor' || role === 'commenter' || role === 'viewer';
}

/**
 * Read the effective role supplied by the projects API. The owner fallback keeps
 * optimistic/new project records writable before their next server refresh.
 */
export function getEffectiveProjectRole(
  project: ProjectAccessSummary,
  user?: { id: string; role?: SystemRole } | null,
): ProjectRole | undefined {
  if (isProjectRole(project.user_role)) return project.user_role;
  if (user?.role === 'admin') return 'owner';
  if (user && project.owner_id === user.id) return 'owner';
  return undefined;
}

export function canEditProject(
  project: ProjectAccessSummary,
  user?: { id: string; role?: SystemRole } | null,
): boolean {
  return hasProjectPermission(getEffectiveProjectRole(project, user), 'task:update');
}

/** Existing task definitions are read-only, including rows cached before this policy. */
export function canEditTask(
  _task: TaskProjectSummary,
  _projects: ProjectAccessSummary[],
  _user?: { id: string; role?: SystemRole } | null,
): boolean {
  void [_task, _projects, _user];
  return false;
}

/** The assigner and assignee may change status until completion. */
export function canCompleteTask(
  task: {
    status?: string;
    creator_id?: string;
    assignee_id?: string;
    permissions?: { can_complete?: boolean };
  },
  user?: { id: string } | null,
): boolean {
  if (!user || task.status === 'completed') return false;
  if (typeof task.permissions?.can_complete === 'boolean') return task.permissions.can_complete;
  return task.creator_id === user.id || task.assignee_id === user.id;
}

// Check if a team role has a specific team permission
export function hasTeamPermission(role: TeamRole | undefined, permission: TeamPermission): boolean {
  if (!role) return false;
  // Owner always has all team permissions
  if (role === 'owner') return true;
  return TeamPermissions[permission]?.includes(role) ?? false;
}

// Check if user is admin
export function isAdmin(role: SystemRole | undefined): boolean {
  return role === 'admin';
}
